import { useEffect } from 'react'

const EMPTY_RESULT_MESSAGE = 'No speech detected — please try again.'
const FAILURE_MESSAGE = 'Transcription failed — please try again.'

interface UseTranscriptionParams {
  audioBlob: Blob | null
  clearAudioBlob: () => void
  transcribeAudio: (blob: Blob) => Promise<string>
  onText: (text: string) => void
  onError: (message: string) => void
}

/**
 * Transcribes a recorded audio blob and routes the outcome to callbacks.
 *
 * Replaces the copy-pasted try/catch effects in the four chat composers with
 * one place that enforces consistent handling: a non-empty transcript fires
 * `onText`, an empty-string result or a thrown error fires `onError` with a
 * user-facing message, and `clearAudioBlob` always runs so the same blob is
 * never re-processed.
 *
 * Callback-stability contract: the effect intentionally depends only on
 * `audioBlob` (exhaustive-deps is disabled) so transcription fires exactly once
 * per recorded blob. Callers MUST pass referentially stable `onText`, `onError`,
 * `transcribeAudio`, and `clearAudioBlob` — wrap them in `useCallback` (or hoist
 * to module scope). A callback whose identity changes on every render is silently
 * ignored until the next blob: its newest closure is NOT picked up mid-transcription.
 * Passing an inline arrow each render is the foot-gun this contract guards against.
 */
export const useTranscription = ({
  audioBlob,
  clearAudioBlob,
  transcribeAudio,
  onText,
  onError,
}: UseTranscriptionParams): void => {
  useEffect(() => {
    if (!audioBlob) return

    const process = async () => {
      try {
        const text = await transcribeAudio(audioBlob)
        if (text) {
          onText(text)
        } else {
          onError(EMPTY_RESULT_MESSAGE)
        }
      } catch (err) {
        console.error('[useTranscription] Transcription failed:', err)
        onError(FAILURE_MESSAGE)
      } finally {
        clearAudioBlob()
      }
    }

    process()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioBlob])
}
